import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { Table, TableProps, Grid, message } from 'antd';
import { SorterResult } from 'antd/lib/table/interface';
import {
  TeamOutput,
  useFindGroupQuery,
  useUpdateDonorMutation,
  DonorOutput,
  Order,
} from 'gql/types';
import { Spin } from 'modules/common/components';
import DonorsTable from 'modules/donor/components/DonorsTable';
import { getTeamsColumns } from 'modules/donor/components/TeamsTable/columns';
import { TeamListSort } from 'modules/donor/components/TeamsTable/interfaces';
import {
  TeamTitleFormSchema,
  teamTitleFormSchema,
} from 'modules/donor/components/TeamsTable/schema';
import { useTeamsSort } from 'modules/donor/hooks/useTeamsSort';
import { FC, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

const { useBreakpoint } = Grid;

const TeamsTable: FC = () => {
  const { t } = useTranslation('donor.TeamsTable');
  const { md } = useBreakpoint();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const teamId = searchParams.get('teamId');
  const groupId = params.id || '';
  const { data: groupData, loading: findGroupLoading } = useFindGroupQuery({
    variables: {
      input: {
        id: groupId,
      },
    },
  });
  const teams = (groupData?.findGroup.teams as TeamOutput[]) || [];
  const { sort, sortedTeams } = useTeamsSort(teams);
  const [currentTeam, setCurrentTeam] = useState<TeamOutput>();
  const [isEditMode, setIsEditMode] = useState(false);

  const teamTitleForm = useForm<TeamTitleFormSchema>({
    resolver: yupResolver(teamTitleFormSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    if (!isEditMode && teamTitleForm.formState.isDirty) {
      teamTitleForm.reset();
    }
  }, [isEditMode]);

  const [updateDonor, { loading: updateDonorLoading }] = useUpdateDonorMutation(
    {
      refetchQueries: ['findGroup', 'getUserDonorsList'],
      onCompleted: () => {
        message.success(t('successfullyUpdated'));
      },
      onError: ({ message: m }) => {
        message.error(m);
      },
    }
  );

  const removeDonorFromTeamHandler = (donor: DonorOutput) => {
    updateDonor({
      variables: {
        input: {
          id: donor.id,
          teamId: null,
        },
      },
    });
  };

  const onTableChange: TableProps<TeamOutput>['onChange'] = (
    _,
    __,
    sorter,
    extra
  ) => {
    if (extra.action === 'sort') {
      const tableSorter = sorter as SorterResult<TeamOutput>;
      const fieldName = tableSorter.columnKey as TeamListSort;
      const order = tableSorter.order === 'ascend' ? Order.Asc : Order.Desc;
      if (fieldName && order && tableSorter.order) {
        sort.setSortOption({ fieldName, order });
      }
      if (!(sorter as any).order) {
        sort.removeSortOption(fieldName);
      }
    }
  };

  const isLoading = findGroupLoading || updateDonorLoading;
  const columns = getTeamsColumns({
    form: teamTitleForm,
    isEditMode: isEditMode,
    onToggleEditMode: setIsEditMode,
    currentTeam,
    setCurrentTeam,
    sort: sort.options[0],
    showSorterTooltip: md,
  });
  const owner = groupData?.findGroup.user;
  return (
    <Spin spinning={isLoading}>
      <Table
        rowKey={({ id }) => id}
        columns={columns}
        dataSource={sortedTeams}
        pagination={false}
        onChange={onTableChange}
        expandable={{
          defaultExpandedRowKeys: teamId ? [teamId] : undefined,
          expandedRowRender: ({ donors }) => {
            const mappedDonors = donors?.map((donor) => {
              return { ...donor, user: owner };
            });
            return (
              <DonorsTable
                withoutSearch
                donors={mappedDonors || []}
                showRemoveColumn={true}
                removeHandler={removeDonorFromTeamHandler}
              />
            );
          },
        }}
      />
    </Spin>
  );
};

export default TeamsTable;
